import { useMemo } from 'react'

import { FINANCING_SETTINGS } from './buybackConst'
import { FinancingStatus } from './buybackFinancingHelpers'
import { useFinancingCalculations } from './useFinancingCalculations'

export interface FinancingStatusResult {
  status: FinancingStatus
  text: string
  className: string
  description: string
}

/**
 * Calculates financing status based on all scenarios
 * @param calculations - object containing scenarios and duration parameters
 * @returns status result with text, className and description
 */
export const useFinancingStatus = (): FinancingStatusResult => {
  const { scenarios, rentDurationMonths } = useFinancingCalculations()

  let minBalance = FINANCING_SETTINGS.balanceWarningLevel

  for (const scenario of scenarios) {
    if (scenario.month <= rentDurationMonths) {
      if (scenario.buybackBalance < minBalance) {
        minBalance = scenario.buybackBalance
      }
      if (scenario.marketBalance < minBalance) {
        minBalance = scenario.marketBalance
      }
    }
  }
  const currentStatus = useMemo(() => {
    if (minBalance <= 0) return FinancingStatus.Rejected
    if (minBalance < FINANCING_SETTINGS.balanceWarningLevel)
      return FinancingStatus.PendingApproval
    return FinancingStatus.Approved
  }, [minBalance])

  const statusResult = useMemo(() => {
    switch (currentStatus) {
      case FinancingStatus.Rejected:
        return {
          status: FinancingStatus.Rejected,
          text: 'Zamítnuto',
          className: 'text-red-400',
          description: 'Financování není dostupné',
        }
      case FinancingStatus.PendingApproval:
        return {
          status: FinancingStatus.PendingApproval,
          text: 'Ke schválení',
          className: 'text-yellow-500',
          description: 'Riskantní, vyžaduje schválení',
        }
      default:
        return {
          status: FinancingStatus.Approved,
          text: 'Předschváleno',
          className: 'text-lime-600',
          description: 'Financování je dostupné',
        }
    }
  }, [currentStatus])

  return statusResult
}
